/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useState, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { DateTimePicker, Button } from 'carbon-addons-iot-react';
import moment from 'moment';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { ChevronRight16, ChevronLeft16 } from '@carbon/icons-react';

import {
  PickerKinds,
  TimeRanges,
  TimeRangeValue,
  TimeRangeOption,
  PALTimeRange,
  PALPresetTimeRange,
  PALAbsoluteTimeRange,
  PALRelativeTimeRange,
} from '../../../Common/types/DateTypes';
import { determineStartDateFromPresetRangeChange } from '../../../Common/utils/cardUtilityFunctions';
import { determineDefaultPresets } from '../../utils/dashboardRendererUtils';

export const dateSelectorMessages = defineMessages({
  last30Minutes: 'Last 30 minutes',
  lastHour: 'Last hour',
  last6Hours: 'Last 6 hours',
  last12Hours: 'Last 12 hours',
  last24Hours: 'Last 24 hours',
  last7Days: 'Last 7 days',
  lastMonth: 'Last month',
  lastQuarter: 'Last quarter',
  lastYear: 'Last year',
  to: 'to',
  toNow: 'to Now',
  calendar: 'Calendar',
  minutes: 'minutes',
  hours: 'hours',
  days: 'days',
  weeks: 'weeks',
  months: 'months',
  years: 'years',
  today: 'Today',
  yesterday: 'Yesterday',
  customRange: 'Custom Range',
  relative: 'Relative',
  last: 'Last',
  invalidNumber: 'Number is not valid',
  relativeTo: 'Relative to',
  absolute: 'Absolute',
  startTime: 'Start time',
  endTime: 'End time',
  applyBtn: 'Apply',
  cancelBtn: 'Cancel',
  backBtn: 'Back',
  increment: 'Increment',
  decrement: 'Decrement',
  number: 'number',
  back: 'Back',
  forward: 'Forward',
});

type startEndDateType = {
  startDate: moment.Moment;
  endDate: moment.Moment;
};

type onDateChangeType = (date: startEndDateType, timeRange?: PALTimeRange) => void;

type DashboardDateSelectorType = {
  /** callback when a timerange is selected */
  onDateChange: onDateChangeType;
  timeRangeOptions: TimeRangeOption[];
  selectedTimeRange: PALTimeRange;
  setSelectedTimeRange: Function;
  startDate: moment.Moment;
  endDate: moment.Moment;
};

export const handleDateRangeChange = (
  timeRangeId: TimeRanges,
  onDateChange: onDateChangeType,
  presetTimeRange: PALTimeRange
): void => {
  const endDate = moment();

  const startDate = determineStartDateFromPresetRangeChange(endDate, timeRangeId);
  onDateChange(
    {
      startDate,
      endDate,
    },
    presetTimeRange
  );
};

export const handleCustomDateRangeChange = (
  dateTimeEvt: TimeRangeValue,
  onDateChange: onDateChangeType,
  customTimeRange: PALTimeRange
): void => {
  const endDate = moment(dateTimeEvt.end);
  const startDate = moment(dateTimeEvt.start);

  onDateChange(
    {
      startDate,
      endDate,
    },
    customTimeRange
  );
};

// Finds the previous range based on the difference between the start and end dates
export const getPreviousRange = ({ startDate, endDate }: startEndDateType): startEndDateType => {
  // Get time difference in milliseconds
  const millisecondsOffset: number = moment.duration(endDate.diff(startDate)).asMilliseconds();
  // Get new date by subtracting the milliseconds offset
  const newStartDate: moment.Moment = startDate
    .clone()
    .subtract(millisecondsOffset, 'milliseconds');
  // newEndDate should just be the old startDate
  const newEndDate: moment.Moment = startDate;
  return { startDate: newStartDate, endDate: newEndDate };
};

// Finds the next range based on the difference between the start and end dates
export const getNextRange = ({ startDate, endDate }: startEndDateType): startEndDateType => {
  // Get time difference in milliseconds
  const millisecondsOffset: number = moment.duration(endDate.diff(startDate)).asMilliseconds();
  // newStartDate should just be the old endDate
  const newStartDate: moment.Moment = endDate;
  // Get new date by adding the milliseconds offset
  const newEndDate: moment.Moment = endDate.clone().add(millisecondsOffset, 'milliseconds');
  return { startDate: newStartDate, endDate: newEndDate };
};

// Parses the PAL range format to return { startDate, endDate }
const getStartEndDateFromPALRange = (newPALRange, selectedRange): startEndDateType => {
  const { timeRangeKind, timeRangeValue } = newPALRange;
  if (timeRangeKind === PickerKinds.PRESET) {
    const newStartDate = determineStartDateFromPresetRangeChange(
      selectedRange.endDate,
      timeRangeValue.id
    );
    return { startDate: newStartDate, endDate: selectedRange.endDate };
  }
  return { startDate: moment(timeRangeValue.start), endDate: moment(timeRangeValue.end) };
};

export const onApply = (
  timeRangeOptions: TimeRangeOption[],
  selectedTimeRange: PALTimeRange,
  setSelectedTimeRange: Function,
  onDateChange: onDateChangeType
): void => {
  const presetTimeRange = selectedTimeRange as PALPresetTimeRange;
  const absoluteTimeRange = selectedTimeRange as PALAbsoluteTimeRange;
  const relativeTimeRange = selectedTimeRange as PALRelativeTimeRange;

  // Preset time range
  if (presetTimeRange.timeRangeKind === PickerKinds.PRESET && presetTimeRange.timeRangeValue?.id) {
    setSelectedTimeRange({
      timeRangeKind: presetTimeRange.timeRangeKind,
      timeRangeValue: omit(
        timeRangeOptions.find(({ id }) => id === presetTimeRange.timeRangeValue.id),
        'start',
        'end'
      ),
    });
    handleDateRangeChange(presetTimeRange.timeRangeValue.id, onDateChange, presetTimeRange);
  }
  // custom time range
  else if (
    absoluteTimeRange.timeRangeKind === PickerKinds.ABSOLUTE &&
    absoluteTimeRange.timeRangeValue
  ) {
    setSelectedTimeRange({
      timeRangeKind: absoluteTimeRange.timeRangeKind,
      timeRangeValue: omit(absoluteTimeRange.timeRangeValue, 'start', 'end'),
    });
    handleCustomDateRangeChange(absoluteTimeRange.timeRangeValue, onDateChange, absoluteTimeRange);
  } else if (
    relativeTimeRange.timeRangeKind === PickerKinds.RELATIVE &&
    relativeTimeRange.timeRangeValue
  ) {
    setSelectedTimeRange({
      timeRangeKind: relativeTimeRange.timeRangeKind,
      timeRangeValue: omit(relativeTimeRange.timeRangeValue, 'start', 'end'),
    });
    handleCustomDateRangeChange(relativeTimeRange.timeRangeValue, onDateChange, relativeTimeRange);
  }
};

const DashboardDateSelector = ({
  onDateChange,
  timeRangeOptions: timeRangeOptionsProp,
  selectedTimeRange,
  setSelectedTimeRange,
  startDate: startDateProp,
  endDate: endDateProp,
}: DashboardDateSelectorType): JSX.Element => {
  const { formatMessage, locale } = useIntl();
  // Need to keep track of the updated ranges for use with the navigation arrows
  const [selectedRange, setSelectedRange] = useState({
    startDate: startDateProp,
    endDate: endDateProp,
  });

  const timeRangeOptions = useMemo(
    () =>
      !isEmpty(timeRangeOptionsProp)
        ? timeRangeOptionsProp
        : determineDefaultPresets(formatMessage),
    [formatMessage, timeRangeOptionsProp]
  );

  // Handles applying the new absolute range to the dashboard when an arrow is clicked
  const handleNextPreviousArrow: onDateChangeType = newDate => {
    const newRange = {
      timeRangeKind: PickerKinds.ABSOLUTE,
      timeRangeValue: {
        start: newDate.startDate.valueOf(),
        end: newDate.endDate.valueOf(),
        startDate: newDate.startDate.format('MM/DD/YYYY'),
        endDate: newDate.endDate.format('MM/DD/YYYY'),
        startTime: newDate.startDate.format('HH:mm'),
        endTime: newDate.endDate.format('HH:mm'),
      },
    };
    onApply(timeRangeOptions, newRange as PALAbsoluteTimeRange, setSelectedTimeRange, onDateChange);
    setSelectedRange(newDate);
  };

  return (
    <>
      <Button
        onClick={(): void => handleNextPreviousArrow(getPreviousRange(selectedRange))}
        hasIconOnly
        kind="ghost"
        tooltipAlignment="center"
        tooltipPosition="bottom"
        iconDescription={formatMessage(dateSelectorMessages.back)}
        renderIcon={ChevronLeft16}
        testId="back-button"
      />
      <DateTimePicker
        id="table-data--date-picker"
        testId="table-data--date-picker"
        dateTimeMask="L HH:mm"
        locale={locale?.split('-')?.[0]}
        showRelativeOption
        defaultValue={selectedTimeRange}
        presets={timeRangeOptions}
        onApply={(newRange: PALTimeRange): void => {
          setSelectedRange(getStartEndDateFromPALRange(newRange, selectedRange));
          onApply(timeRangeOptions, newRange, setSelectedTimeRange, onDateChange);
        }}
        i18n={{
          toLabel: formatMessage(dateSelectorMessages.to),
          toNowLabel: formatMessage(dateSelectorMessages.toNow),
          calendarLabel: formatMessage(dateSelectorMessages.calendar),
          presetLabels: [
            formatMessage(dateSelectorMessages.last30Minutes),
            formatMessage(dateSelectorMessages.lastHour),
            formatMessage(dateSelectorMessages.last6Hours),
            formatMessage(dateSelectorMessages.last12Hours),
            formatMessage(dateSelectorMessages.last24Hours),
            formatMessage(dateSelectorMessages.last7Days),
            formatMessage(dateSelectorMessages.lastMonth),
            formatMessage(dateSelectorMessages.lastQuarter),
            formatMessage(dateSelectorMessages.lastYear),
          ],
          intervalLabels: [
            formatMessage(dateSelectorMessages.minutes),
            formatMessage(dateSelectorMessages.hours),
            formatMessage(dateSelectorMessages.days),
            formatMessage(dateSelectorMessages.weeks),
            formatMessage(dateSelectorMessages.months),
            formatMessage(dateSelectorMessages.years),
          ],
          relativeLabels: [
            formatMessage(dateSelectorMessages.today),
            formatMessage(dateSelectorMessages.yesterday),
          ],
          customRangeLinkLabel: formatMessage(dateSelectorMessages.customRange),
          customRangeLabel: formatMessage(dateSelectorMessages.customRange),
          relativeLabel: formatMessage(dateSelectorMessages.relative),
          lastLabel: formatMessage(dateSelectorMessages.last),
          invalidNumberLabel: formatMessage(dateSelectorMessages.invalidNumber),
          relativeToLabel: formatMessage(dateSelectorMessages.relativeTo),
          absoluteLabel: formatMessage(dateSelectorMessages.absolute),
          startTimeLabel: formatMessage(dateSelectorMessages.startTime),
          endTimeLabel: formatMessage(dateSelectorMessages.endTime),
          applyBtnLabel: formatMessage(dateSelectorMessages.applyBtn),
          cancelBtnLabel: formatMessage(dateSelectorMessages.cancelBtn),
          backBtnLabel: formatMessage(dateSelectorMessages.backBtn),
          increment: formatMessage(dateSelectorMessages.increment),
          decrement: formatMessage(dateSelectorMessages.decrement),
          hours: formatMessage(dateSelectorMessages.hours),
          minutes: formatMessage(dateSelectorMessages.minutes),
          number: formatMessage(dateSelectorMessages.number),
        }}
        hasTimeInput
        light
      />
      <Button
        onClick={(): void => handleNextPreviousArrow(getNextRange(selectedRange))}
        hasIconOnly
        kind="ghost"
        tooltipAlignment="center"
        tooltipPosition="bottom"
        iconDescription={formatMessage(dateSelectorMessages.forward)}
        renderIcon={ChevronRight16}
        testId="forward-button"
      />
    </>
  );
};

export default DashboardDateSelector;
